import { memo, useMemo } from 'react';
import { capitalize } from 'lodash-es';
import { PracticeAnalyticsAggregations, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { DemoChip } from '@frontend/analytics-shared';
import { Chart, sequentialChartColors } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { collectAllInsuranceLabels } from './active-patients-metric-multi';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';

type Props = PracticeAnalyticsTypes.MetricProps & {
  data?: ReturnType<typeof PracticeAnalyticsAggregations.activePatientsDetails>;
  isLoading?: boolean;
};

export const ActivePatientsMetricSingle = memo(({ data, isLoading }: Props) => {
  const { t } = useTranslation('analytics');
  const { showDemoChipAndBanner } = usePracticeAnalyticsShallowStore('showDemoChipAndBanner');

  const chartData = useMemo(() => {
    const { patientDemographics } = data?.aggregatedData || {};
    const insuranceLabels = [...new Set(collectAllInsuranceLabels(data))];
    const totals: Record<string, number> = {
      male: 0,
      female: 0,
      insured: 0,
    };

    const ageGender = {
      groups:
        patientDemographics?.buckets?.map(({ label, subbuckets }) => ({
          name: label,
          values: subbuckets.reduce((acc, { label, total }) => {
            const key = label.toLowerCase();

            if (key === 'other') {
              return acc;
            }

            totals[key] += total;
            return { ...acc, [label.toLowerCase()]: total };
          }, {}),
        })) || [],
    };

    const insurance = {
      groups:
        patientDemographics?.details?.insuranceProviders.map(({ label, total }) => {
          totals.insured += total;
          return {
            name: label,
            values: {
              [label]: total,
            },
          };
        }) || [],
    };

    return {
      ageGender,
      insurance,
      insuranceColors: insuranceLabels.reduce(
        (acc, label, index) => ({ ...acc, [label]: sequentialChartColors[index] || theme.colors.neutral5 }),
        {}
      ),
      insuranceLabels: insuranceLabels.reduce((acc, label) => ({ ...acc, [label]: capitalize(label) }), {}),
      totals,
    };
  }, [data]);

  const commonAppearance = useMemo(() => {
    return {
      collectiveTooltip: true,
      showGridLines: true,
      showXAxis: true,
      showYAxis: true,
    };
  }, []);

  return (
    <>
      <Chart
        colors={{
          female: theme.colors.warning50,
          male: theme.colors.secondary.seaweed30,
        }}
        isLoading={isLoading}
        labels={{
          female: t('Female'),
          male: t('Male'),
        }}
      >
        <Chart.Header leftElement={showDemoChipAndBanner ? <DemoChip /> : null} title={t('Patients by Age/Gender')} />
        <Chart.BarChart appearance={commonAppearance} data={chartData.ageGender} formatValue={formatters.value} />
        <Chart.Legends formatValue={formatters.value} values={chartData.totals} />
      </Chart>

      <Chart
        colors={chartData.insuranceColors}
        commonTooltipLabel={t('Patients')}
        isLoading={isLoading}
        labels={chartData.insuranceLabels}
      >
        <Chart.Header leftElement={showDemoChipAndBanner ? <DemoChip /> : null} title={t('Patients by Insurance')} />
        <Chart.BarChart appearance={commonAppearance} data={chartData.insurance} formatValue={formatters.value} />
        <Chart.Legends
          customData={{
            insured: {
              color: theme.colors.primary40,
              label: t('Total'),
              value: formatters.value(chartData.totals.insured),
            },
          }}
        />
      </Chart>
    </>
  );
});

ActivePatientsMetricSingle.displayName = 'ActivePatientsMetricSingle';
